import { useState, useCallback, useRef } from 'react';
import {
  query,
  collection,
  orderBy,
  limit,
  startAfter,
  getDocs,
  QueryConstraint,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

interface UsePaginationOptions {
  pageSize?: number;
  initialPage?: number;
}

export function usePagination<T>(
  collectionName: string,
  options: UsePaginationOptions = {},
  ...constraints: QueryConstraint[]
) {
  const { pageSize = 10, initialPage = 1 } = options;

  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [hasPrevPage, setHasPrevPage] = useState(false);

  // 페이지별 마지막 문서 저장 - useRef로 변경하여 의존성 문제 해결
  const lastDocsRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData>>>(new Map());
  const firstDocsRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData>>>(new Map());

  // constraints를 문자열로 변환하여 비교용으로 사용
  const constraintsKey = JSON.stringify(constraints.map((c) => c.toString()));

  const loadPage = useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        let q = query(
          collection(db, collectionName),
          ...constraints,
          orderBy('createdAt', 'desc'),
          limit(pageSize + 1) // 다음 페이지 존재 여부 확인을 위해 +1
        );

        // 첫 페이지가 아닌 경우
        if (page > 1) {
          const prevPageLastDoc = lastDocsRef.current.get(page - 1);
          if (prevPageLastDoc) {
            q = query(q, startAfter(prevPageLastDoc));
          }
        }

        const snapshot = await getDocs(q);
        const docs = snapshot.docs;

        // 실제 페이지 크기만큼만 데이터 추출
        const hasMore = docs.length > pageSize;
        const pageData = docs.slice(0, pageSize);

        const mappedData = pageData.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as T[];

        setItems(mappedData);
        setHasNextPage(hasMore);
        setHasPrevPage(page > 1);
        setCurrentPage(page);

        // 문서 참조 저장
        if (pageData.length > 0) {
          lastDocsRef.current.set(page, pageData[pageData.length - 1]);
          firstDocsRef.current.set(page, pageData[0]);
        }
      } catch (error) {
        console.error('Pagination error:', error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collectionName, pageSize, constraintsKey]
  );

  const loadFirstPage = useCallback(() => {
    lastDocsRef.current = new Map();
    firstDocsRef.current = new Map();
    loadPage(1);
  }, [loadPage]);

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !loading) {
      loadPage(currentPage + 1);
    }
  }, [currentPage, hasNextPage, loading, loadPage]);

  const loadPrevPage = useCallback(() => {
    if (hasPrevPage && !loading) {
      loadPage(currentPage - 1);
    }
  }, [currentPage, hasPrevPage, loading, loadPage]);

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && !loading) {
        loadPage(page);
      }
    },
    [loading, loadPage]
  );

  return {
    items,
    loading,
    currentPage,
    hasNextPage,
    hasPrevPage,
    loadFirstPage,
    loadNextPage,
    loadPrevPage,
    goToPage,
  };
}
